import { createActor } from 'xstate';
import { threeDownOneUpEngine, } from '@labrain/engine-staircase';
import { createBus, } from './bus';
import { makeGameShellMachine } from './machine';
import { createPolishLayer } from './polish';
const DEFAULT_ITI_MS = 700;
/**
 * Sleep avec abort signal — utilitaire interne.
 */
function delay(ms, abortFlag) {
    return new Promise((resolve) => {
        if (abortFlag.aborted)
            return resolve();
        const t = setTimeout(() => resolve(), ms);
        // Polling abort — Sprint 0.5 simple, sinon AbortSignal API.
        const tick = setInterval(() => {
            if (abortFlag.aborted) {
                clearTimeout(t);
                clearInterval(tick);
                resolve();
            }
        }, 50);
        // S'auto-cleanup si setTimeout résout normalement.
        setTimeout(() => clearInterval(tick), ms + 60);
    });
}
/**
 * Crée un controller game run. Le caller est responsable de :
 *   - appeler `controller.start()` (démarre l'actor en phase `intro`),
 *   - appeler `controller.startSession(sessionId)` quand l'utilisateur clique "Démarrer",
 *   - appeler `controller.stop()` à l'unmount React.
 */
export function createGameRun(opts) {
    const { config, adapter, buildStimulus, grader, bus = createBus(), itiMs = DEFAULT_ITI_MS, calibration, priorStaircaseState = null, modulateResponse, } = opts;
    const machine = makeGameShellMachine(config);
    // Actor + subscription sont mutables — start()/stop() peuvent être appelés
    // plusieurs fois (React 18 StrictMode dev double-mount, route navigation).
    // Une fois un actor `stopped`, xstate v5 ne le relance pas en place : il faut
    // en recréer un. Voir buildActorAndSubscribe() / start().
    let actor = createActor(machine, { input: { config } });
    let actorSub = null;
    let actorStarted = false;
    const polish = createPolishLayer(adapter, {
        enableVisual: config.enableVisualFeedback,
        enableAudio: config.enableSound,
        enableHaptic: config.enableHaptics,
        reducedMotion: config.reducedMotion ?? false,
        gameMaster: config.gameMaster ?? 'air',
        ...opts.polishOptions,
    });
    // Engine staircase state — pure, possédé par le runtime, immuté à chaque trial.
    let staircaseState = threeDownOneUpEngine.init(config.staircaseConfig, calibration, priorStaircaseState);
    const flags = {
        paused: false,
        aborted: false,
        loopRunning: false,
        pauseStart: 0,
        totalPausedMs: 0,
    };
    let phasePrev = 'intro';
    const snapshotListeners = new Set();
    // Snapshot cache : on évite de retourner un nouvel objet à chaque getSnapshot,
    // sinon `useSyncExternalStore` boucle (référence ≠ même quand rien n'a changé).
    let cachedSnapshot;
    function rebuildSnapshot() {
        const ctx = actor.getSnapshot().context;
        cachedSnapshot = {
            phase: ctx.phase,
            context: ctx,
            staircase: staircaseState,
            isPaused: flags.paused,
            isAborted: flags.aborted,
        };
        return cachedSnapshot;
    }
    cachedSnapshot = rebuildSnapshot();
    function makeSnapshot() {
        return cachedSnapshot;
    }
    function emitSnapshot() {
        const snap = rebuildSnapshot();
        for (const l of snapshotListeners) {
            try {
                l(snap);
            }
            catch (err) {
                // eslint-disable-next-line no-console
                console.error('[game-shell runtime] snapshot listener threw', err);
            }
        }
    }
    // Bridge actor → bus + snapshot listeners. Recréable via buildSubscription().
    function buildSubscription() {
        return actor.subscribe((state) => {
            const ph = state.context.phase;
            if (ph !== phasePrev) {
                bus.emit({
                    type: 'PHASE_CHANGED',
                    from: phasePrev,
                    to: ph,
                    ts: Date.now(),
                });
                phasePrev = ph;
                if (ph === 'staircase' && !flags.loopRunning && !flags.aborted) {
                    // Lance la boucle staircase une seule fois.
                    flags.loopRunning = true;
                    runStaircaseLoop().catch((err) => {
                        // eslint-disable-next-line no-console
                        console.error('[game-shell runtime] staircase loop crashed', err);
                        flags.loopRunning = false;
                    });
                }
            }
            emitSnapshot();
        });
    }
    actorSub = buildSubscription();
    async function waitWhilePaused() {
        while (flags.paused && !flags.aborted) {
            await new Promise((r) => setTimeout(r, 50));
        }
    }
    async function runStaircaseLoop() {
        while (!staircaseState.done && !flags.aborted) {
            await waitWhilePaused();
            if (flags.aborted)
                return;
            const engStim = threeDownOneUpEngine.getNextStimulus(staircaseState);
            const tStim = buildStimulus(engStim, engStim.trialIdx);
            let tResp;
            try {
                tResp = await adapter.presentStimulus(tStim);
            }
            catch (err) {
                // adapter rejected (abort ou crash). On termine la boucle proprement.
                if (!flags.aborted) {
                    // eslint-disable-next-line no-console
                    console.warn('[game-shell runtime] presentStimulus rejected', err);
                }
                return;
            }
            if (flags.aborted)
                return;
            let graded = grader(tStim, tResp);
            if (modulateResponse)
                graded = modulateResponse(tStim, graded);
            // Polish (visuel + audio + haptic en parallèle).
            try {
                await polish.applyTrialFeedback(graded.correct);
            }
            catch (err) {
                // eslint-disable-next-line no-console
                console.warn('[game-shell runtime] polish feedback threw', err);
            }
            const submit = threeDownOneUpEngine.submitResponse(staircaseState, graded);
            staircaseState = submit.state;
            // Notify la machine xstate.
            actor.send({
                type: 'TRIAL_COMPLETED',
                correct: graded.correct,
                rt: graded.reactionTimeMs,
                rawInput: graded.rawInput,
            });
            bus.emit({
                type: 'TRIAL_COMPLETED',
                gameSlug: config.gameSlug,
                trialIdx: engStim.trialIdx,
                correct: graded.correct,
                rt: graded.reactionTimeMs,
                stimulusValue: engStim.value,
                ts: Date.now(),
            });
            emitSnapshot();
            if (submit.runDone) {
                actor.send({
                    type: 'STAIRCASE_DONE',
                    threshold: staircaseState.thresholdEstimate,
                });
                bus.emit({
                    type: 'GAME_RUN_COMPLETED',
                    gameSlug: config.gameSlug,
                    threshold: staircaseState.thresholdEstimate,
                    ts: Date.now(),
                });
                return;
            }
            // ITI delay (préempt par abort).
            await delay(itiMs, flags);
        }
    }
    function start() {
        // StrictMode-safe : si l'actor a été stoppé (cycle stop/start React 18 dev
        // double-mount, navigation route), on en recrée un avec une nouvelle
        // subscription. snapshotListeners (consumers React) sont préservés —
        // useSyncExternalStore se ré-abonne séparément.
        // NB : xstate v5 status='active' dès createActor (avant .start()). On ne
        // peut pas baser le check sur status, donc on track explicitement avec
        // `actorStarted`.
        if (actor.getSnapshot().status === 'stopped') {
            actor = createActor(machine, { input: { config } });
            phasePrev = 'intro';
            flags.aborted = false;
            flags.paused = false;
            flags.loopRunning = false;
            actorStarted = false;
            // staircaseState garde son état (continuité runs successifs voulue).
            actorSub = buildSubscription();
        }
        if (!actorStarted) {
            actor.start();
            actorStarted = true;
            cachedSnapshot = rebuildSnapshot();
            emitSnapshot();
        }
    }
    function stop() {
        flags.aborted = true;
        try {
            actorSub?.unsubscribe();
        }
        catch {
            /* déjà unsubscribed */
        }
        actorSub = null;
        actorStarted = false;
        // snapshotListeners NE SONT PAS clearés — les consumers React (useSyncExternalStore)
        // gèrent leur propre cycle subscribe/unsubscribe.
        try {
            actor.stop();
        }
        catch {
            /* déjà stop */
        }
    }
    function startSession(sessionId = null) {
        bus.emit({
            type: 'GAME_RUN_STARTED',
            gameSlug: config.gameSlug,
            sessionId,
            ts: Date.now(),
        });
        actor.send({ type: 'START', sessionId });
    }
    function pause(reason = 'user') {
        if (flags.paused || flags.aborted)
            return;
        flags.paused = true;
        flags.pauseStart = Date.now();
        adapter.pause?.();
        actor.send({ type: 'PAUSE', reason });
        bus.emit({ type: 'PAUSED', reason, ts: Date.now() });
        emitSnapshot();
    }
    function resume() {
        if (!flags.paused || flags.aborted)
            return;
        const pausedDurationMs = Date.now() - flags.pauseStart;
        flags.totalPausedMs += pausedDurationMs;
        flags.paused = false;
        flags.pauseStart = 0;
        adapter.resume?.();
        actor.send({ type: 'RESUME' });
        bus.emit({ type: 'RESUMED', pausedDurationMs, ts: Date.now() });
        emitSnapshot();
    }
    function abort(reason = 'user') {
        if (flags.aborted)
            return;
        flags.aborted = true;
        flags.paused = false;
        adapter.abort?.();
        actor.send({ type: 'ABORT', reason });
        bus.emit({
            type: 'GAME_RUN_ABORTED',
            gameSlug: config.gameSlug,
            reason,
            ts: Date.now(),
        });
        emitSnapshot();
    }
    return {
        // Getter — `actor` est mutable interne (recréé après stop/start cycle),
        // mais l'interface publique reste readonly. Les consumers qui cachent
        // une référence doivent re-lire `ctrl.actor` après chaque cycle.
        get actor() {
            return actor;
        },
        bus,
        adapter,
        start,
        stop,
        startSession,
        // Helpers : indirection via `actor` (rebound par closure courante chaque appel).
        skipDemo: () => {
            actor.send({ type: 'SKIP_DEMO' });
        },
        markDemoDone: () => {
            actor.send({ type: 'DEMO_DONE' });
        },
        markWarmupDone: () => {
            actor.send({ type: 'WARMUP_DONE' });
        },
        markWrapUpDone: () => {
            actor.send({ type: 'WRAP_UP_DONE' });
        },
        complete: () => {
            actor.send({ type: 'COMPLETE' });
        },
        pause,
        resume,
        abort,
        getSnapshot: makeSnapshot,
        subscribe(listener) {
            snapshotListeners.add(listener);
            // Push initial snapshot immédiat.
            listener(makeSnapshot());
            return () => {
                snapshotListeners.delete(listener);
            };
        },
    };
}
//# sourceMappingURL=runtime.js.map